import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Sparkles } from 'lucide-react';

import type { ClassificationResponse } from '../api';
import { bandStyle } from './classificationMeta';

interface ClassificationBadgeProps {
  classification: ClassificationResponse;
}

/**
 * Reviewer badge for an auto-classified document: «Clasificado automáticamente · confianza
 * alta/media/baja», tinted per band (emerald/amber/rose). Click opens a popover listing the evidence
 * fragments the classifier cited and the runner-up type it considered — the provenance a reviewer needs
 * to trust (or distrust) the automatic type before approving.
 */
export function ClassificationBadge({ classification }: ClassificationBadgeProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const style = bandStyle(classification.confidenceBand);

  // Close on outside click / Escape so the popover behaves like a menu.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointer = (event: MouseEvent): void => {
      if (containerRef.current !== null && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const hasDetails = classification.evidence.length > 0 || classification.runnerUpSlug !== null;

  return (
    <div className="relative inline-block" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup={hasDetails ? 'dialog' : undefined}
        data-testid="classification-badge"
        className={clsx(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
          style.chip,
          hasDetails ? 'cursor-pointer' : 'cursor-default',
        )}
        title="Tipo detectado automáticamente"
      >
        <Sparkles className="h-3.5 w-3.5" aria-hidden strokeWidth={2} />
        Clasificado automáticamente · confianza {style.label}
      </button>

      {open && hasDetails ? (
        <div
          role="dialog"
          aria-label="Detalle de la clasificación automática"
          className="absolute left-0 top-full z-20 mt-1 w-72 rounded-lg border border-slate-200 bg-white p-3 text-left shadow-lg"
          data-testid="classification-badge-popover"
        >
          {classification.runnerUpSlug !== null ? (
            <p className="text-xs text-slate-600">
              Alternativa considerada:{' '}
              <span className="font-medium text-slate-900">{classification.runnerUpSlug}</span>
            </p>
          ) : null}
          {classification.evidence.length > 0 ? (
            <div className={classification.runnerUpSlug !== null ? 'mt-2' : ''}>
              <p className="text-xs font-medium text-slate-500">Evidencia</p>
              <ul className="mt-1 space-y-1">
                {classification.evidence.map((fragment, index) => (
                  <li
                    key={index}
                    className="rounded bg-slate-50 px-2 py-1 text-xs text-slate-700"
                  >
                    “{fragment}”
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {classification.classifiedBy !== null ? (
            <p className="mt-2 border-t border-slate-100 pt-2 font-mono text-[10px] text-slate-400">
              {classification.classifiedBy}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
